import { alpha } from "@mui/material/styles";
import { theme } from "@zesty-io/material";
import { memo, useState } from "react";

import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  Typography,
  Box,
  TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import useIsMounted from "ismounted";
import { useDispatch, useSelector } from "react-redux";

import { request } from "utility/request";
import { notify } from "shell/store/notifications";
import { useTranslation } from "react-i18next";

export default memo(function WorkflowRequest({ itemTitle, fields }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const user = useSelector((state) => state.user);
  const users = useSelector((state) => state.users);
  const instance = useSelector((state) => state.instance);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);

  function handleSelectField(evt, val) {
    const { name } = evt.target;
    // push name to state
    if (val) {
      setSelectedFields([...selectedFields, name]);
    } else {
      setSelectedFields(selectedFields.filter((field) => field !== name));
    }
  }

  function handleSelectUser(evt, val) {
    // push name to state
    const { name } = evt.target;
    if (val) {
      setSelectedMembers([...selectedMembers, name]);
    } else {
      setSelectedMembers(selectedMembers.filter((user) => user !== name));
    }
  }

  function handleSend() {
    const body = `
<p><strong>${
      user.firstName + " " + user.lastName
    }</strong> has sent you a workflow request for <a href="${
      window.location.href
    }">${itemTitle}</a> on ${instance.name}.</p>

${
  message
    ? `<blockquote style="border-left: 3px solid #444;padding: 0 12px;">${message}</blockquote>`
    : ""
}

${
  selectedFields.length
    ? `<p>Fields to review:</p>
  <ul>
  ${selectedFields.map((field) => `<li>${field}</li>`).join("")}
  </ul>`
    : ""
}

<p><small>Direct Link: <a href="${window.location.href}">${
      window.location.href
    }</a></small></p>
      `;

    const subject = `Workflow Request on ${instance.name} from ${user.firstName}`;
    const to = selectedMembers.join(", ");

    setSending(true);

    request(`${CONFIG.CLOUD_FUNCTIONS_DOMAIN}/sendEmail`, {
      json: true,
      body: {
        body,
        subject,
        to,
      },
    })
      .then((res) => {
        if (res.status === 200) {
          dispatch(
            notify({
              message: t("content.itemEditWorkflowRequestSent", {
                title: itemTitle,
                members: selectedMembers.join(", "),
              }),
              kind: "save",
            })
          );
        } else {
          dispatch(
            notify({
              message: t("content.itemEditWorkflowRequestFailed", {
                title: itemTitle,
                members: selectedMembers.join(", "),
              }),
              kind: "warn",
            })
          );
        }
      })
      .catch((err) => console.error("Failed to send workflow request:", err))
      .finally(() => {
        if (isMounted.current) {
          setSending(false);
        }
      });
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Accordion elevation={0} sx={{ backgroundColor: "transparent" }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: "transparent",
            p: 0,
            borderBottom: 1,
            borderColor: "grey.200",
            minHeight: "0px",
            height: "32px",
            ".MuiAccordionSummary-content": {
              m: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "16px",
              color: alpha(theme.palette.text.primary, 0.4),
              ".MuiSvgIcon-root": {
                mr: 1,
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontWeight: 400,
                color: "text.primary",
              }}
              textTransform="uppercase"
            >
              {t("content.itemEditWorkflowRequestTitle")}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{ display: "flex", flexDirection: "column", gap: 2, p: 0, mt: 2 }}
        >
          <Box component="div">
            <Typography component="h3" variant="h6">
              {t("content.itemEditSelectTeamMembers")}
            </Typography>
            <Box component="ul">
              {users.map((user) => (
                <Box component="li" key={user.ZUID} sx={{ listStyle: "none" }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          size="small"
                          name={user.email}
                          onChange={(evt, val) => handleSelectUser(evt, val)}
                        />
                      }
                      label={`${user.firstName} ${user.lastName}`}
                    />
                  </FormGroup>
                </Box>
              ))}
            </Box>

            <Typography component="h3" variant="h6" sx={{ my: 2 }}>
              {t("content.itemEditSelectFieldsForReview")}
            </Typography>
            <Box component="ul">
              {fields?.map((field) => (
                <Box component="li" key={field.ZUID} sx={{ listStyle: "none" }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          size="small"
                          name={field.name}
                          onChange={(evt, val) => handleSelectField(evt, val)}
                        />
                      }
                      label={field.label}
                    />
                  </FormGroup>
                </Box>
              ))}
            </Box>

            <TextField
              multiline
              fullWidth
              name="message"
              placeholder={t("content.itemEditWorkflowRequestMessage")}
              value={message}
              onChange={(evt) => setMessage(evt.target.value)}
              rows={4}
            />
          </Box>
        </AccordionDetails>
        <AccordionActions>
          <Button
            variant="contained"
            id="WorkflowRequestSendButton"
            onClick={handleSend}
            disabled={sending}
            startIcon={
              sending ? <CircularProgress size="20px" /> : <SaveIcon />
            }
          >
            {t("content.itemEditSendEmail")}
          </Button>
        </AccordionActions>
      </Accordion>
    </Box>
  );
});
